#!/usr/bin/env node

import { log } from "./log";
import { ensureLoggedIn } from "./auth";
import { fetchAlerts } from "./alerts";
import {
  writeDefaultRotationsConfigFile,
  pushConfiguration,
  hasRotationsConfigFile,
  readConfigurationFile,
  fetchRotations,
} from "@/cli/configurations";
import { getGitEmails } from "@/cli/git";
import { ensureHasSubscription } from "@/cli/checkout";
import ora from "ora";
import type { ApiSubscriptionResponse } from "@/pages/api/cli/subscription";
import { getBaseUrl } from "@/config";

async function setup() {
  const { token, userId, email } = await ensureLoggedIn();

  if (!userId || !token) {
    log.error("Not logged in");
    process.exit(1);
  }

  // TODO move subscriptions stuff to checkout.ts

  // Check subscription status using the CLI auth client
  // Since the Better Auth client doesn't have a direct fetch method, we'll use the regular fetch
  const subscriptionResponse = await fetch(
    `${getBaseUrl({ prodUrl: !process.env.BEEPS_DEVELOPMENT })}/api/cli/subscription`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!subscriptionResponse.ok) {
    log.error("Failed to check subscription status");
    process.exit(1);
  }

  const subscriptionData =
    (await subscriptionResponse.json()) as ApiSubscriptionResponse;

  if (!subscriptionData.ok || !subscriptionData.hasActiveSubscription) {
    const success = await ensureHasSubscription(email || "", token);
    if (!success) {
      log.error("Could not set up subscription");
      process.exit(1);
    }
  }

  const command = process.argv[2];

  if (command === "alerts") {
    // Use the fetchAlerts function instead of direct API call
    return fetchAlerts(token);
  }

  const hasConfigFile = await hasRotationsConfigFile();
  if (hasConfigFile) {
    const config = readConfigurationFile();
    if (!config) {
      log.error("Failed to read beeps.json");
      process.exit(1);
    }

    // Push the configuration using pushConfiguration
    await pushConfiguration(token, userId, config);
    log.success("\n  beeps.json pushed");

    // Fetch rotations using fetchRotations
    return fetchRotations(token);
  }

  // Continue with engineer selection
  log.title("\n👥 Setting Up Rotation");
  log.info("\nFetching engineers from git history...");
  const gitEmails = await getGitEmails();

  // Filter out bot emails and extract just the email addresses
  const emails = gitEmails
    .filter(({ isBot }) => !isBot)
    .map(({ email }) => email);

  // Select engineers for rotation
  const { select: selectEngineers } = await import("inquirer-select-pro");
  const selectedMembers = await selectEngineers<string>({
    message: "Select engineers for the rotation:",
    options: emails.map((email) => ({ value: email, name: email })),
    multiple: true,
    clearInputWhenSelected: true,
    canToggleAll: true,
    defaultValue: emails,
  });

  if (!selectedMembers.length) {
    log.error("You must select at least one engineer for the rotation");
    process.exit(1);
  }

  await writeDefaultRotationsConfigFile({
    emails: selectedMembers,
  });

  // Use the pushConfiguration function to create rotations
  try {
    const config = {
      rotations: [
        {
          name: "default",
          members: selectedMembers,
          startDate: new Date().toISOString().split("T")[0]!,
          frequency: "daily" as const,
        },
      ],
    };

    await pushConfiguration(token, userId, config);
  } catch (error) {
    log.error("Failed to create rotations");
    process.exit(1);
  }

  const spinner = ora("Processing configuration...").start();
  await new Promise((resolve) => setTimeout(resolve, 3000));
  spinner.stop();

  // Fetch rotations using fetchRotations
  return fetchRotations(token);
}

setup().catch((error) => {
  console.error("Error during setup:", error);
  process.exit(1);
});
